// @ts-strict-ignore
import keyBy from 'lodash/keyBy';
import omit from 'lodash/omit';
import zip from 'lodash/zip';

import {lexicalCompose} from 'common/util/function';

import {Section, SectionItem, WrappedSection, WrappedSectionItem} from './types';

/**
 * Finds selected items on sections
 */
export function findSelectedItems<T = any>(
  sections: Section[],
  predicate: (item: SectionItem) => boolean = (item) => item.isSelected,
): Array<SectionItem<T>> {
  const selection = new Set<SectionItem>();

  for (const section of sections) {
    if (section.items) {
      for (const item of section.items) {
        if (predicate(item)) {
          selection.add(item);
        }
      }
    }
  }

  return Array.from(selection);
}

/**
 * Finds selected wrapped items on wrapped sections
 */
export function findSelectedWrappedItems<T = any>(
  sections: WrappedSection[],
  predicate: (item: WrappedSectionItem) => boolean = (item) => item.wrapped.isSelected,
): Array<WrappedSectionItem<T>> {
  const selection = new Set<WrappedSectionItem>();

  for (const section of sections) {
    if (section.items) {
      for (const item of section.items) {
        if (predicate(item)) {
          selection.add(item);
        }
      }
    }
  }

  return Array.from(selection);
}

export function itemHasSubSections(item: SectionItem): boolean {
  if (item.isDisabled) {
    return false;
  }

  return Boolean(item?.subSections?.some(isPopulatedSection) || item?.subMenuRenderer);
}

function isPopulatedSection(section: Section): boolean {
  return Boolean(section.items?.length || section.isLoading);
}

export function areSectionsEmpty(sections: Section[]): boolean {
  return sections.length === 0 || sections.every((section) => !isPopulatedSection(section));
}

function keyForSectionItem({label}: SectionItem): string {
  // Gracefully handle bad data from consumers, some items are being passed without a label
  return typeof label === `string` ? label.toLowerCase().trim() : ``;
}

export function createItemsKeyMap(sections: Section[]): {[key: string]: SectionItem} {
  return keyBy(
    sections.flatMap((s) => s.items),
    keyForSectionItem,
  );
}

export function sortOnProperty(matchProp: string) {
  const getMatch = (item) => (item[matchProp] ? item[matchProp] : {score: -1, matchStr: ``});

  return lexicalCompose(
    // fuzzy match score
    (a, b) => getMatch(b).score - getMatch(a).score,
    // alphabetical
    (a, b) => getMatch(a).matchStr.localeCompare(getMatch(b).matchStr),
  );
}

export function getVisibleSections(sections: WrappedSection[]): WrappedSection[] {
  return sections.filter((section) => !section.isHidden);
}

export function wrapSectionItem(item: SectionItem, {sectionIndex}: {sectionIndex: number}): WrappedSectionItem {
  return {
    wrapped: item,
    sectionIndex,
  };
}

export function wrapSections(sections: Section[]): WrappedSection[] {
  return sections.map((section, sectionIndex) => ({
    ...section,
    sectionIndex,
    items: section.items?.map((item) => wrapSectionItem(item, {sectionIndex})),
  }));
}

export function addIndicesToWrappedItems(sections: WrappedSection[]): void {
  let itemIndex = 0;
  sections.forEach((section) => {
    section.items.forEach((item) => {
      item.index = itemIndex++;
    });
  });
}

function unwrapSection(section: WrappedSection): Section {
  return {
    ...omit(section, `items`, `sectionIndex`),
    items: section.items.map((item) => item.wrapped),
  };
}

export function unwrapSections(sections: WrappedSection[]): Section[] {
  return sections.map(unwrapSection);
}

export function countTotalItems(sections: Array<Section | WrappedSection>) {
  return sections.reduce((numItems, section) => numItems + section.items.length, 0);
}

/**
 * Comparing if sections have changed is tricky because there can be non-primitive types within `sections`, which
 * will be considered changed using lodash's isEqual, even if the sections haven't changed. An example is
 * inline functions whose reference change when `sections` are recomputed, even though it's the same implementation.
 * So the following is just a heuristic to check is sections have changed. it won't detect all cases of `sections`
 * changing, but it's good enough to use for the autofocusing behavior.
 */
export function shouldAutoFocusOnSectionsChange(
  prevSections: Section[] | undefined,
  nextSections: Section[] | undefined,
): boolean {
  if (prevSections?.length !== nextSections?.length) {
    return true;
  }
  return zip(prevSections, nextSections).some(([prevSection, nextSection]) => {
    return prevSection?.items?.length !== nextSection?.items?.length;
  });
}
